import { hotkeys } from 'react-keyboard-shortcuts';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ActionText from '../../core/ActionText';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import PagingFilter from '../../core/PagingFilter';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles } from '../../core/style/gstheme';
import { makeCancelable } from '../../core/utils/utils';
import { sortLabels } from '../utils/utils';
import GS from '../../grafsync/grafsync';
import LabelData from '../skosxl/LabelData';
import XLstructure from '../skosxl/XLstructure';

const styles = Object.assign({}, globalStyles);

class FloatingLabelView extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      initialSearch: true,
      labels: [],
      lang: this.props.defaultLang,
      text: "",
      page: 1,
      noneFound: false,
      noResults: true,
      pageCount: null,
      isErrored: null
    };
    this.infoRef = React.createRef();
    this.controlRef = React.createRef();
    this.cancellable = null;
  }

  hot_keys = {
    'alt+i': {
      priority: 1,
      handler: () => {
        if (this.infoRef && this.infoRef.current) {
          this.infoRef.current.focus();
        }
      }
    },
    'alt+c': {
      priority: 1,
      handler: () => {
        if (this.controlRef && this.controlRef.current) {
          this.controlRef.current.focus();
        }
      }
    }
  };

  componentDidMount() {
    this.handlePageChange(1, true);
  }

  handlePageRefresh = () => {
    this.setState({ text: "", initialSearch: true }, () => this.handlePageChange(this.state.page));
  };

  handleFilterChange = (text) => {
    this.setState({ text, initialSearch: text === "" }, () => this.handlePageChange(this.state.page, text === ""));
  };

  onSelectLanguage = (lang) => {
    this.setState({ lang, initialSearch: true }, () => this.handlePageChange(this.state.page, true));
  };

  /** We include 'fast' as a property to do the initial search without ordering
   * For very large sets of labels without this performance is too slow
   */
  handlePageChange = async (page = 1, initialSearch = false) => {
    if (!initialSearch && this.state.text.length < 2) return;
    this.setState(
      { isLoading: true },
      () => {
        let search = [{
          predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
          value: "http://www.w3.org/2008/05/skos-xl#Label"
        }];
        if (this.state.text !== "") {
          search.push({
            predicate: "http://www.w3.org/2008/05/skos-xl#literalForm",
            value: this.state.text,
            isExactMatch: false
          });
        }
        else {
          search.push({
            predicate: "http://www.w3.org/2008/05/skos-xl#literalForm"
          });
        }
        this.doSearch({
          search: search,
          noCount: true,
          retainOrder: initialSearch,
          ...(initialSearch) && { fast: true },
          excludeByPath: [
            {
              predicate: [
                "http://www.w3.org/2004/02/skos/core#inScheme"
              ]
            }
          ],
          excludeByPathOnly: true,
          allowMissingResults: true,
          graph: [this.props.graph],
          exclude: this.props.deletedLabels,
          language: this.state.lang,
          predicates: ["http://www.w3.org/2008/05/skos-xl#literalForm"],
          page: page,
          pageSize: 50,
          sort: ["http://www.w3.org/2008/05/skos-xl#literalForm"]
        })
          .promise
          .then(data => {
            if (data.results && data.results.length > 0) {
              const sortedLabels = sortLabels(data.results);
              const labelIDs = sortedLabels.map(result => result.id);
              this.loadData(labelIDs, {})
                .promise
                .then(() => this.setState({
                  labels: labelIDs,
                  page,
                  noResults: false,
                  noneFound: false,
                  isLoading: false
                }))
                .catch((e) => {
                  if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                });
            }
            else {
              this.setState({
                isLoading: false,
                page: 1,
                noneFound: true,
                noResults: false
              });
            }
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      });
  };

  static getDerivedStateFromProps(props, state) {
    if (!state.isLoading && props.updateAt !== state.refreshAt) {
      return {
        labels: null,
        noResults: true,
        initialSearch: true,
        refreshAt: props.updateAt
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoading && this.state.labels === null && this.state.noResults === true) {
      this.handlePageChange(this.state.page, this.state.initialSearch);
    }
  }

  doSearch(payload) {
    this.cancellable = makeCancelable(
      GS.search(payload)
    );
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(
      GS.loadData(iris, options, this.props.graph)
    );
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  handleChange = name => event => {
    this.setState({ [name]: event.target.checked }, () => this.handlePageChange(this.state.page, this.state.text === ""));
  };

  render() {

    const { isLoading, labels, text, isErrored, noneFound } = this.state;
    const { activeLabel, deletedLabels, onSelect, onDeleteLabel, classes, schemeIRI, appBar, graph, entitlements, project } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    return (
      <ProjectConsumer>
        {({ projectLang }) => (
          <div>
            <div className={classes.controlPanel}>
              <div className={classes.views}>
                {appBar}
              </div>
              <div className={classes.controlPanelInner}>
                <div className={classes.controlPanelSearch} style={{ marginLeft: 30 }}>
                  <PagingFilter
                    autoFocus={true}
                    handleFilterChange={this.handleFilterChange}
                    text={text}
                    maxLength={50} />
                </div>
              </div>
              <div tabIndex={0} ref={this.controlRef} className={classes.tree} style={{ top: '170px' }}>
                {projectLang.length > 1 &&
                  <div style={{ marginLeft: 30, width: 'calc(100% - 40px)' }}>
                    <ProjectLanguageSelector includeLabel={false} onSelect={this.onSelectLanguage} />
                  </div>
                }
                {
                  isLoading ?
                    <Loading offset="100px" message={text === "" ? <span>If this takes more than a few seconds<br />try filtering.</span> : null} />
                    :
                    <XLstructure
                      labels={labels}
                      graph={graph}
                      noneFound={noneFound}
                      activeLabel={activeLabel}
                      deletedLabels={deletedLabels}
                      id={schemeIRI}
                      onSelect={onSelect} />
                }
              </div>
            </div>
            <div ref={this.infoRef} tabIndex={0} data-test="info-panel" className={classes.infoPanel}>
              {activeLabel === null ?
                <ActionText offset="180px">Select a label to view the detail.</ActionText>
                :
                <LabelData
                  key={activeLabel}
                  project={project}
                  graph={graph}
                  entitlements={entitlements}
                  labelIRI={activeLabel}
                  onDeleteLabel={onDeleteLabel} />
              }
            </div>
          </div>
        )}
      </ProjectConsumer>
    );
  }

}

FloatingLabelView.propTypes = {
  classes: PropTypes.object,
  appBar: PropTypes.node.isRequired,
  graph: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  updateAt: PropTypes.number,
  entitlements: PropTypes.array.isRequired,
  activeLabel: PropTypes.string,
  schemeIRI: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onDeleteLabel: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  deletedLabels: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default withStyles(styles)(hotkeys(FloatingLabelView));